/**
 * StationMediaManager - Main container for station media management
 * Orchestrates Icon, Banner, and Gallery sections
 */

import React from 'react';
import { Box, Stack, Divider, Typography, Skeleton, Alert } from '@mui/material';
import {
  useStationImages,
  useUploadStationImage,
  useDeleteStationImage,
  useReorderGallery,
} from '../../hooks/stationImages';
import { StationMediaManagerProps, IMAGE_TYPE_CONFIGS } from './types';
import IconSection from './IconSection';
import BannerSection from './BannerSection';
import GallerySection from './GallerySection';
import CustomToast from '../Redesing/custom-toast';

const StationMediaManager: React.FC<StationMediaManagerProps> = ({
  stationId,
  stationName,
  disabled = false,
  readOnly = false,
  compact = false,
}) => {
  // Fetch existing images (include pending for replacement flow visibility)
  const {
    data: images,
    isLoading,
    error: fetchError,
  } = useStationImages(stationId, {
    enabled: Boolean(stationId),
    includePendingReview: true,
    includeRejected: true,
  });

  // Upload hook
  const { uploadImage, uploadMultiple, uploadingImages, isUploading } =
    useUploadStationImage(stationId);

  // Delete hook
  const deleteImageMutation = useDeleteStationImage(stationId, {
    onSuccess: () => {
      CustomToast({
        type: 'success',
        message: 'Image deleted successfully',
      });
    },
    onError: (error) => {
      CustomToast({
        type: 'error',
        message: error.message || 'Failed to delete image',
      });
    },
  });

  // Reorder hook
  const reorderMutation = useReorderGallery(stationId, {
    onError: (error) => {
      CustomToast({
        type: 'error',
        message: error.message || 'Failed to reorder images',
      });
    },
  });

  // Handler for icon upload
  const handleIconUpload = async (file: File) => {
    const result = await uploadImage(file, 'icon');
    if (result.success) {
      if (result.status === 'pending_review') {
        CustomToast({
          type: 'success',
          message: 'Icon uploaded. Awaiting admin approval.',
        });
      } else {
        CustomToast({
          type: 'success',
          message: 'Icon uploaded successfully',
        });
      }
    } else {
      CustomToast({
        type: 'error',
        message: result.error || 'Failed to upload icon',
      });
    }
  };

  // Handler for banner upload
  const handleBannerUpload = async (file: File) => {
    const result = await uploadImage(file, 'banner');
    if (result.success) {
      if (result.status === 'pending_review') {
        CustomToast({
          type: 'success',
          message: 'Banner uploaded. Awaiting admin approval.',
        });
      } else {
        CustomToast({
          type: 'success',
          message: 'Banner uploaded successfully',
        });
      }
    } else {
      CustomToast({
        type: 'error',
        message: result.error || 'Failed to upload banner',
      });
    }
  };

  // Handler for gallery upload (bulk)
  const handleGalleryUpload = async (files: File[]) => {
    const results = await uploadMultiple(files, 'gallery');
    const successful = results.filter((r) => r.success).length;
    const failed = results.filter((r) => !r.success).length;

    if (successful > 0 && failed === 0) {
      CustomToast({
        type: 'success',
        message: `${successful} image${successful > 1 ? 's' : ''} uploaded successfully`,
      });
    } else if (successful > 0 && failed > 0) {
      CustomToast({
        type: 'error',
        message: `${successful} uploaded, ${failed} failed`,
      });
    } else if (failed > 0) {
      CustomToast({
        type: 'error',
        message: `Failed to upload ${failed} image${failed > 1 ? 's' : ''}`,
      });
    }
  };

  // Handler for delete
  const handleDelete = async (imageId: string, imageType: 'icon' | 'banner' | 'gallery') => {
    await deleteImageMutation.mutateAsync({ imageId, imageType });
  };

  // Handler for reorder
  const handleReorder = async (imageIds: string[]) => {
    await reorderMutation.mutateAsync(imageIds);
  };

  // Get uploading images by type
  const iconUploading = uploadingImages.find((img) => img.type === 'icon');
  const bannerUploading = uploadingImages.find((img) => img.type === 'banner');
  const galleryUploading = uploadingImages.filter((img) => img.type === 'gallery');

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Stack spacing={4}>
          {/* Icon skeleton */}
          <Box>
            <Skeleton variant="text" width={120} height={28} />
            <Skeleton variant="text" width={200} height={20} sx={{ mb: 2 }} />
            <Stack direction="row" spacing={3}>
              <Skeleton variant="rounded" width={120} height={120} />
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <Skeleton variant="rounded" width="100%" height={140} />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Banner skeleton */}
          <Box>
            <Skeleton variant="text" width={140} height={28} />
            <Skeleton variant="text" width={220} height={20} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" width="100%" height={160} />
          </Box>

          <Divider />

          {/* Gallery skeleton */}
          <Box>
            <Skeleton variant="text" width={150} height={28} />
            <Skeleton variant="text" width={260} height={20} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" width="100%" height={180} />
          </Box>
        </Stack>
      </Box>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load station images: {fetchError.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      {!compact && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Media Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage images for {stationName || 'this station'}. Images are reviewed for
            appropriate content before being published.
          </Typography>
        </Box>
      )}

      <Stack spacing={4}>
        {/* Icon Section */}
        <IconSection
          stationId={stationId}
          currentImage={images?.icon || null}
          pendingImage={images?.pending?.icon}
          rejectedImage={images?.rejected?.icon}
          uploadingImage={iconUploading}
          onUpload={handleIconUpload}
          onDelete={(id) => handleDelete(id, 'icon')}
          disabled={disabled || isUploading}
          readOnly={readOnly}
        />

        <Divider />

        {/* Banner Section */}
        <BannerSection
          stationId={stationId}
          currentImage={images?.banner || null}
          pendingImage={images?.pending?.banner}
          rejectedImage={images?.rejected?.banner}
          uploadingImage={bannerUploading}
          onUpload={handleBannerUpload}
          onDelete={(id) => handleDelete(id, 'banner')}
          disabled={disabled || isUploading}
          readOnly={readOnly}
        />

        <Divider />

        {/* Gallery Section */}
        <GallerySection
          stationId={stationId}
          images={images?.gallery || []}
          uploadingImages={galleryUploading}
          onUpload={handleGalleryUpload}
          onDelete={(id) => handleDelete(id, 'gallery')}
          onReorder={handleReorder}
          maxCount={IMAGE_TYPE_CONFIGS.gallery.maxCount}
          disabled={disabled || isUploading}
          readOnly={readOnly}
        />
      </Stack>
    </Box>
  );
};

export default StationMediaManager;
